import { UseQueryOptions } from '@tanstack/react-query';
import { GraphQLClient, gql } from 'graphql-request';

import { GraphQLSupporter } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { isObject } from '@sola/shared-utils/misc.js';

import { MOCK_SUPPORTER, QUERY_KEYS } from './constants';

const graphqlEndpoint =
  process.env.GRAPHQL_PROXY_DESTINATION ||
  `${process.env.NEXT_PUBLIC_BASE_URL_PROXY_API}/graphql`;

/**
 * GraphQL Client
 * If needbe, we can configure error handling in the middleware.
 */
const client = new GraphQLClient(graphqlEndpoint, {
  credentials: 'include',
});

type GraphQLErrorResponse = {
  message: string;
};

export const isGraphQLResponseError = (
  response: unknown,
): response is GraphQLErrorResponse => {
  return isObject(response) && 'message' in response;
};

type SelectedSupporterData<TKey> = TKey extends keyof GraphQLSupporter
  ? Pick<GraphQLSupporter, TKey>
  : Pick<GraphQLSupporter, keyof GraphQLSupporter>;

// const mockCommitments = process.env.NEXT_PUBLIC_USE_MOCK_SUPPORTER
//   ? MOCK_COMMITMENTS_MIXED
//   : undefined;

// const mockGivingHistory = process.env.NEXT_PUBLIC_USE_MOCK_SUPPORTER
//   ? MOCK_SUPPORTER_GIVING_HISTORY
//   : undefined;

/**
 * Response from a graphql query.
 * It allows us to specify the data being returned, which is generally recommended.
 */
type SupporterResponse<
  TKey extends keyof GraphQLSupporter | undefined = undefined,
> =
  | {
      supporter: SelectedSupporterData<TKey>;
      // null is to handle errors, double check this.
    }
  | GraphQLErrorResponse;

export type SupporterQuery<
  TResponseData extends keyof GraphQLSupporter | undefined = undefined,
> = Omit<UseQueryOptions, 'queryFn'> & {
  queryFn: (
    globalId: string,
  ) => Promise<
    TResponseData extends keyof GraphQLSupporter
      ? GraphQLSupporter[TResponseData] | [] | null
      : SelectedSupporterData<TResponseData>
  >;
};
/**
 * Get all payment method information.
 */
export const getPaymentMethods = async (globalId: string) => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.paymentMethods;
  }

  const response: SupporterResponse<'paymentMethods'> = await client.request(
    gql`
      query GetSupporterPaymentMethods($globalId: String!) {
        supporter(globalId: $globalId) {
          paymentMethods {
            id
            status
            accountNickname
            billingAddress {
              attention
              street
              city
              stateCode
              postalCode
              country
            }
            paymentType
            isPrimary
            creditCard {
              cardholderName
              cardType
              digitalType
              expirationDate
              expiryStatus
              lastFour
              responseCode
              token
            }
            ach {
              accountName
              lastFourOfAccountNumber
              routingNumber
              token
            }
          }
        }
      }
    `,
    { globalId },
  );

  return isGraphQLResponseError(response) || !response.supporter?.paymentMethods
    ? []
    : response.supporter.paymentMethods;
};

/**
 * Query all payment methods.
 * // TODO, clean up TS once we get things going.
 */
export const paymentMethodsQuery: SupporterQuery<'paymentMethods'> = {
  queryKey: [QUERY_KEYS.supporterPaymentMethods],
  queryFn: (globalId) => getPaymentMethods(globalId),
};

/**
 * Get suppoerter address
 */
export const getSupporterAddresses = async (globalId: string) => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.address;
  }

  const response: SupporterResponse<'address'> = await client.request(
    gql`
      query GetSupporterAddress($globalId: String!) {
        supporter(globalId: $globalId) {
          address {
            street
            street2
            city
            state
            postalCode
            country
            invalid
          }
        }
      }
    `,
    { globalId },
  );

  return isGraphQLResponseError(response) || !response.supporter?.address
    ? undefined
    : response.supporter.address;
};

export const supporterAddressesQuery: SupporterQuery<'address'> = {
  queryKey: [QUERY_KEYS.getSupporterAddresses],
  queryFn: (globalId) => getSupporterAddresses(globalId),
};

/**
 * Get all commitments.
 *
 * // TODO possibly reduce to what we know we actually need.
 */
export const getCommitments = async (globalId: string) => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.commitments;
  }

  const response: SupporterResponse<'commitments'> = await client.request(
    gql`
      query GetSupporterCommitments($globalId: String!) {
        supporter(globalId: $globalId) {
          commitments {
            id
            participant {
              birthdate
              country
              gender
              globalId
              localId
              name
              photoUrl
            }
            commitmentType
            dates {
              start
              end
            }
            fund {
              code
              description
              icon
            }
            internalId
            interventionId
            isCorrespondent
            isRecurring
            lastCorrespondenceDate
            paymentDateLast
            paymentDateNext
            hasMissedPayment
            paymentFrequency
            paymentMethodId
            paymentPullDay
            rate
            receiptorId
            sourceCode
            status
            transactionId
          }
        }
      }
    `,
    { globalId },
  );
  return isGraphQLResponseError(response) || !response.supporter?.commitments
    ? []
    : response.supporter.commitments;
};

/**
 * Query all commitment data
 * // TODO, clean up TS once we get things going.
 */
export const commitmentsQuery: SupporterQuery<'commitments'> = {
  queryKey: [QUERY_KEYS.supporterCommitments],
  queryFn: (globalId) => getCommitments(globalId),
};

export const getGivingTotals = async (globalId: string) => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.givingTotals;
  }

  const response: SupporterResponse<'givingTotals'> = await client.request(
    gql`
      query GetSupporterGivingTotals($globalId: String!) {
        supporter(globalId: $globalId) {
          givingTotals {
            annual
            monthly
            quarterly
            semiAnnual
          }
        }
      }
    `,
    { globalId },
  );
  return isGraphQLResponseError(response) || !response.supporter?.givingTotals
    ? []
    : response.supporter.givingTotals || {};
};

/**
 * Query giving totals
 */
export const givingTotalsQuery: SupporterQuery = {
  queryKey: ['supporterGivingTotals'],
  // @ts-expect-error TODO improve SupporterQuery type
  queryFn: async (globalId: string) => getGivingTotals(globalId),
};
